import { Link } from "@tanstack/react-router";
import type { Product } from "@/data/products";
import { ANIMAL_LABELS, CATEGORY_LABELS, formatPrice } from "@/data/products";
import { AnimalIcon, CategoryIcon } from "./icons/BrandIcons";
import { SafeImage } from "./SafeImage";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { cn } from "@/lib/utils";

export function ProductCard({
  product,
  compact = false,
  onClick,
}: {
  product: Product;
  compact?: boolean;
  /**
   * Se fornito, sostituisce la navigazione: utile per aprire il
   * ProductModal (es. dalla pagina ricetta) invece di andare alla scheda.
   */
  onClick?: (slug: string) => void;
}) {
  const inner = (
    <>
      <div className="aspect-square overflow-hidden bg-brand-cream-dark">
        <SafeImage
          src={product.image}
          alt={product.name}
          className="zoom-img w-full h-full object-cover"
        />
      </div>
      <div className={cn("p-2.5 md:p-4 flex flex-col gap-3", compact && "p-2 md:p-3")}>
        <h3
          className={cn(
            "font-display text-brand-charcoal leading-tight line-clamp-2 min-h-[2.6em]",
            compact ? "text-[13px] md:text-[15px]" : "text-[14px] md:text-base"
          )}
        >
          {product.name}
        </h3>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 md:gap-2 min-w-0">
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <span
                  aria-label={ANIMAL_LABELS[product.animal]}
                  className="inline-flex items-center justify-center text-brand-red"
                >
                  <AnimalIcon
                    animal={product.animal}
                    size={compact ? 18 : 22}
                    strokeWidth={1.8}
                    className="md:!w-7 md:!h-7"
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent>{ANIMAL_LABELS[product.animal]}</TooltipContent>
            </Tooltip>
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <span
                  aria-label={CATEGORY_LABELS[product.category]}
                  className="inline-flex items-center justify-center text-brand-green"
                >
                  <CategoryIcon
                    category={product.category}
                    size={compact ? 18 : 22}
                    strokeWidth={1.8}
                    className="md:!w-7 md:!h-7"
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent>{CATEGORY_LABELS[product.category]}</TooltipContent>
            </Tooltip>
          </div>
          <span
            className={cn(
              "font-numeric font-bold text-brand-charcoal whitespace-nowrap shrink-0",
              compact ? "text-[13px] md:text-base" : "text-[14px] md:text-lg"
            )}
          >
            {formatPrice(product)}
          </span>
        </div>
      </div>
    </>
  );

  const baseClass =
    "group block bg-white rounded-md overflow-hidden lift-on-hover zoom-img-parent shadow-[var(--shadow-soft)] text-left w-full";

  if (onClick) {
    return (
      <button type="button" onClick={() => onClick(product.slug)} className={baseClass}>
        {inner}
      </button>
    );
  }

  return (
    <Link
      to="/prodotti/$slug"
      params={{ slug: product.slug }}
      className={baseClass}
    >
      {inner}
    </Link>
  );
}
